import React from "react"

// Fake "product UI" illustrations rendered as pure SVG. Used as landing-page
// placeholders until real screenshots exist — no fake data needed, scales
// crisply, and themes automatically (light/dark) via Tailwind classes. Each
// variant draws abstract app chrome (cards, bars, rows) that signals "this is a
// real product" without showing literal data.
//
// viewBox is 16:9 (320×180) to match the `aspect-video` container exactly —
// no letterboxing, no crop.

const W = 320
const H = 180
const FRAME = `0 0 ${W} ${H}`

function Chrome({ children, pad = 12 }) {
  // Window chrome: rounded panel + traffic-light dots + toolbar.
  return (
    <>
      <rect x="0" y="0" width={W} height={H} className="fill-white dark:fill-gray-900" />
      <circle cx="16" cy="15" r="3" className="fill-gray-300 dark:fill-gray-600" />
      <circle cx="26" cy="15" r="3" className="fill-gray-300 dark:fill-gray-600" />
      <circle cx="36" cy="15" r="3" className="fill-gray-300 dark:fill-gray-600" />
      <rect x="232" y="11" width="76" height="8" rx="4" className="fill-gray-100 dark:fill-gray-800" />
      <line x1="0" y1="29" x2={W} y2="29" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" />
      <g transform={`translate(${pad}, ${29 + pad})`}>{children}</g>
    </>
  )
}

// All variant content is drawn inside a 296×127 content area (after chrome+pad).

function Dashboard() {
  const bars = [22, 34, 28, 44, 38, 54, 48, 66, 60, 78]
  return (
    <Chrome>
      {/* sidebar */}
      <rect x="0" y="0" width="52" height="127" rx="7" className="fill-gray-50 dark:fill-gray-800/60" />
      {[0, 1, 2, 3].map((r) => (
        <rect key={r} x="9" y={12 + r * 22} width="34" height="7" rx="3.5" className={r === 0 ? "fill-brand-500" : "fill-gray-200 dark:fill-gray-700"} />
      ))}
      {/* KPI cards — name real metrics so the hero signals what Appdesh tracks */}
      {["Payments", "MRR", "Installs", "Churn"].map((kpi, c) => (
        <g key={c}>
          <rect x={64 + c * 60} y="0" width="52" height="40" rx="7" className="fill-gray-50 dark:fill-gray-800/60" />
          <text x={70 + c * 60} y="15" className="fill-gray-400 dark:fill-gray-500" fontSize="6.5" fontWeight="600">{kpi}</text>
          <rect x={70 + c * 60} y="21" width="34" height="10" rx="3" className="fill-brand-400 dark:fill-brand-500" />
        </g>
      ))}
      {/* trend chart */}
      <rect x="64" y="48" width="148" height="79" rx="7" className="fill-gray-50 dark:fill-gray-800/60" />
      <polyline points="76,108 96,92 116,98 136,76 156,84 176,60 196,68" fill="none" className="stroke-brand-500" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {[76, 116, 156, 196].map((x, i) => (
        <circle key={i} cx={x} cy={[108, 98, 84, 68][i]} r="2.5" className="fill-brand-500" />
      ))}
      {/* bar chart */}
      <rect x="220" y="48" width="76" height="79" rx="7" className="fill-gray-50 dark:fill-gray-800/60" />
      {bars.map((h, i) => (
        <rect key={i} x={228 + i * 6.6} y={119 - h} width="4" height={h} rx="2" className="fill-brand-300 dark:fill-brand-600" />
      ))}
    </Chrome>
  )
}

function Analytics() {
  // Traffic funnel: descending stages + source legend.
  const stages = [276, 210, 150, 92]
  const labels = [62, 50, 38]
  return (
    <Chrome>
      <rect x="0" y="2" width="90" height="8" rx="4" className="fill-gray-200 dark:fill-gray-700" />
      {stages.map((w, i) => (
        <rect key={i} x={(296 - w) / 2} y={24 + i * 22} width={w} height="15" rx="5" className="fill-brand-400 dark:fill-brand-500" opacity={1 - i * 0.16} />
      ))}
      {labels.map((w, i) => (
        <g key={i}>
          <circle cx={4} cy={120} r="3.5" className={["fill-brand-500", "fill-amber-400", "fill-gray-300 dark:fill-gray-600"][i]} transform={`translate(${i * 96}, 0)`} />
          <rect x={12 + i * 96} y="116" width={w} height="7" rx="3.5" className="fill-gray-200 dark:fill-gray-700" />
        </g>
      ))}
    </Chrome>
  )
}

function Crm() {
  return (
    <Chrome>
      {/* profile header */}
      <circle cx="16" cy="16" r="14" className="fill-brand-100 dark:fill-brand-900" />
      <circle cx="16" cy="12" r="5" className="fill-brand-400 dark:fill-brand-500" />
      <path d="M6 24 a10 8 0 0 1 20 0 z" className="fill-brand-400 dark:fill-brand-500" />
      <rect x="40" y="8" width="100" height="8" rx="4" className="fill-gray-200 dark:fill-gray-700" />
      <rect x="40" y="21" width="64" height="6" rx="3" className="fill-gray-100 dark:fill-gray-700" />
      <rect x="214" y="9" width="38" height="15" rx="7.5" className="fill-brand-50 dark:fill-brand-950 stroke-brand-300 dark:stroke-brand-700" strokeWidth="1" />
      <rect x="258" y="9" width="38" height="15" rx="7.5" className="fill-amber-50 dark:fill-amber-950 stroke-amber-300 dark:stroke-amber-700" strokeWidth="1" />
      {/* contact rows */}
      {[0, 1, 2, 3].map((r) => (
        <g key={r}>
          <rect x="0" y={44 + r * 21} width="296" height="16" rx="5" className="fill-gray-50 dark:fill-gray-800/60" />
          <circle cx="12" cy={52 + r * 21} r="5" className="fill-gray-200 dark:fill-gray-700" />
          <rect x="24" y={48 + r * 21} width={80 + r * 16} height="7" rx="3.5" className="fill-gray-200 dark:fill-gray-700" />
          <rect x="262" y={48 + r * 21} width="26" height="7" rx="3.5" className="fill-brand-300 dark:fill-brand-600" />
        </g>
      ))}
    </Chrome>
  )
}

function Team() {
  // Member rows with role badges + a dashed "invite" tile.
  const rows = [
    { role: "Owner", w: 70 },
    { role: "Admin", w: 60 },
    { role: "Member", w: 64 },
  ]
  return (
    <Chrome>
      {rows.map((row, r) => (
        <g key={r}>
          <rect x="0" y={r * 30} width="296" height="24" rx="7" className="fill-gray-50 dark:fill-gray-800/60" />
          <circle cx="16" cy={12 + r * 30} r="9" className="fill-brand-400 dark:fill-brand-500" />
          <rect x="32" y={7 + r * 30} width="90" height="7" rx="3.5" className="fill-gray-200 dark:fill-gray-700" />
          <rect x="32" y={16 + r * 30} width="56" height="5" rx="2.5" className="fill-gray-100 dark:fill-gray-700" />
          <rect x={266 - row.w} y={9 + r * 30} width={row.w} height="14" rx="7" className={r === 0 ? "fill-brand-50 dark:fill-brand-950 stroke-brand-300 dark:stroke-brand-700" : "fill-gray-100 dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700"} strokeWidth="1" />
        </g>
      ))}
      {/* invite tile */}
      <rect x="0" y="94" width="296" height="26" rx="7" fill="none" className="stroke-brand-300 dark:stroke-brand-700" strokeWidth="1.5" strokeDasharray="4 4" />
      <circle cx="16" cy="107" r="8" className="fill-brand-50 dark:fill-brand-950" />
      <path d="M12 107 h8 M16 103 v8" className="stroke-brand-500" strokeWidth="1.5" strokeLinecap="round" />
      <rect x="32" y="103" width="110" height="8" rx="4" className="fill-brand-200 dark:fill-brand-800" />
    </Chrome>
  )
}

function Helpdesk() {
  return (
    <Chrome>
      {/* ticket list */}
      <rect x="0" y="0" width="104" height="127" rx="7" className="fill-gray-50 dark:fill-gray-800/60" />
      {[0, 1, 2, 3].map((r) => (
        <g key={r}>
          <circle cx="14" cy={14 + r * 30} r="6" className={r === 0 ? "fill-brand-500" : "fill-gray-200 dark:fill-gray-700"} />
          <rect x="26" y={7 + r * 30} width="66" height="7" rx="3.5" className="fill-gray-200 dark:fill-gray-700" />
          <rect x="26" y={18 + r * 30} width="46" height="6" rx="3" className="fill-gray-100 dark:fill-gray-700" />
        </g>
      ))}
      {/* conversation */}
      <rect x="120" y="8" width="120" height="20" rx="7" className="fill-gray-100 dark:fill-gray-800" />
      <rect x="146" y="40" width="120" height="20" rx="7" className="fill-brand-500" />
      <rect x="120" y="72" width="96" height="20" rx="7" className="fill-gray-100 dark:fill-gray-800" />
      <rect x="120" y="106" width="176" height="16" rx="8" className="fill-gray-50 dark:fill-gray-800/60 stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" />
    </Chrome>
  )
}

function Experiments() {
  // Two toggle switches (on/off) + parallel A/B group bars feeding a branch.
  return (
    <Chrome>
      {/* toggle row */}
      <rect x="0" y="0" width="296" height="44" rx="7" className="fill-gray-50 dark:fill-gray-800/60" />
      <rect x="12" y="10" width="70" height="7" rx="3.5" className="fill-gray-200 dark:fill-gray-700" />
      <rect x="200" y="8" width="34" height="16" rx="8" className="fill-brand-500" />
      <circle cx="226" cy="16" r="6" className="fill-white" />
      <rect x="12" y="27" width="70" height="7" rx="3.5" className="fill-gray-200 dark:fill-gray-700" />
      <rect x="200" y="25" width="34" height="16" rx="8" className="fill-gray-200 dark:fill-gray-700" />
      <circle cx="208" cy="33" r="6" className="fill-white dark:fill-gray-500" />
      {/* branch decision diamond */}
      <path d="M18 76 l10 -10 10 10 -10 10 z" className="fill-amber-100 dark:fill-amber-900 stroke-amber-400 dark:stroke-amber-600" strokeWidth="1.5" />
      <path d="M28 66 V60 H70" fill="none" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="2" />
      <path d="M28 86 V96 H70" fill="none" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="2" />
      {/* group A/B bars */}
      <rect x="70" y="52" width="120" height="18" rx="6" className="fill-brand-400 dark:fill-brand-500" />
      <rect x="80" y="58" width="16" height="6" rx="3" className="fill-white/70" />
      <rect x="70" y="88" width="90" height="18" rx="6" className="fill-gray-200 dark:fill-gray-700" />
      <rect x="80" y="94" width="16" height="6" rx="3" className="fill-gray-400 dark:fill-gray-500" />
      {/* result bars */}
      <rect x="214" y="52" width="70" height="18" rx="6" className="fill-gray-50 dark:fill-gray-800/60" />
      <rect x="222" y="58" width="42" height="6" rx="3" className="fill-brand-400 dark:fill-brand-500" />
      <rect x="214" y="88" width="70" height="18" rx="6" className="fill-gray-50 dark:fill-gray-800/60" />
      <rect x="222" y="94" width="28" height="6" rx="3" className="fill-gray-300 dark:fill-gray-600" />
    </Chrome>
  )
}

function Integrations() {
  // Hub-and-spoke: central node connected to outer abstract app icons.
  const cx = 148
  const cy = 64
  const nodes = [[40, 26], [256, 30], [36, 104], [258, 100]]
  return (
    <Chrome>
      {nodes.map(([x, y], i) => (
        <line key={i} x1={cx} y1={cy} x2={x} y2={y} className="stroke-brand-200 dark:stroke-brand-800" strokeWidth="2" />
      ))}
      {nodes.map(([x, y], i) => (
        <g key={i}>
          <rect x={x - 13} y={y - 13} width="26" height="26" rx="8" className="fill-gray-50 dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="1.5" />
          <circle cx={x} cy={y} r="5" className={i === 0 ? "fill-amber-400" : "fill-brand-300 dark:fill-brand-600"} />
        </g>
      ))}
      <circle cx={cx} cy={cy} r="22" className="fill-brand-50 dark:fill-brand-950 stroke-brand-400 dark:stroke-brand-600" strokeWidth="2" />
      <circle cx={cx} cy={cy} r="9" className="fill-brand-500" />
    </Chrome>
  )
}

function GrowthLoop() {
  // Square 360° growth loop: 5 stage nodes on a circle, arc arrows, center hub.
  const S = 220
  const cx = S / 2
  const cy = S / 2
  const R = 78 // node ring radius
  const r = 22 // node radius
  const steps = ["Acquire", "Understand", "Support", "Retain", "Grow"]
  const accents = [
    "fill-brand-500",
    "fill-brand-400 dark:fill-brand-500",
    "fill-amber-400 dark:fill-amber-500",
    "fill-brand-300 dark:fill-brand-600",
    "fill-brand-500",
  ]
  // node centers, starting at top, clockwise
  const pts = steps.map((_, i) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / steps.length
    return [cx + R * Math.cos(a), cy + R * Math.sin(a), a]
  })

  // arc segment between consecutive nodes, trimmed to sit between node edges
  function arc(i) {
    const [, , a0] = pts[i]
    const [, , a1raw] = pts[(i + 1) % steps.length]
    const a1 = a1raw < a0 ? a1raw + 2 * Math.PI : a1raw
    const gap = (r + 6) / R // angular padding so arc clears nodes
    const s = a0 + gap
    const e = a1 - gap
    const sx = cx + R * Math.cos(s)
    const sy = cy + R * Math.sin(s)
    const ex = cx + R * Math.cos(e)
    const ey = cy + R * Math.sin(e)
    return { d: `M ${sx} ${sy} A ${R} ${R} 0 0 1 ${ex} ${ey}`, ex, ey, ea: e }
  }

  return (
    <svg viewBox={`0 0 ${S} ${S}`} className="w-full h-full">
      <circle cx={cx} cy={cy} r={R} fill="none" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1.5" strokeDasharray="3 5" />
      {/* connecting arrows */}
      {steps.map((_, i) => {
        const { d, ex, ey, ea } = arc(i)
        // arrowhead rotated tangent (+90°) at arc end
        const deg = ((ea + Math.PI / 2) * 180) / Math.PI
        return (
          <g key={i}>
            <path d={d} fill="none" className="stroke-brand-300 dark:stroke-brand-700" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M0 -4 L5 0 L0 4 Z" transform={`translate(${ex} ${ey}) rotate(${deg})`} className="fill-brand-300 dark:fill-brand-700" />
          </g>
        )
      })}
      {/* center hub */}
      <circle cx={cx} cy={cy} r="26" className="fill-brand-50 dark:fill-brand-950 stroke-brand-200 dark:stroke-brand-800" strokeWidth="1.5" />
      <text x={cx} y={cy - 2} textAnchor="middle" className="fill-brand-600 dark:fill-brand-300" style={{ fontSize: 15, fontWeight: 700 }}>360°</text>
      <text x={cx} y={cy + 12} textAnchor="middle" className="fill-gray-400 dark:fill-gray-500" style={{ fontSize: 6.5, letterSpacing: 1, textTransform: "uppercase" }}>GROWTH</text>
      {/* stage nodes */}
      {pts.map(([x, y], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r={r} className="fill-white dark:fill-gray-900 stroke-gray-200 dark:stroke-gray-700" strokeWidth="1.5" />
          <circle cx={x} cy={y - 6} r="4.5" className={accents[i]} />
          <text x={x} y={y + 11} textAnchor="middle" className="fill-gray-600 dark:fill-gray-300" style={{ fontSize: 7, fontWeight: 600 }}>{steps[i]}</text>
        </g>
      ))}
    </svg>
  )
}

const VARIANTS = {
  dashboard: Dashboard,
  analytics: Analytics,
  crm: Crm,
  team: Team,
  helpdesk: Helpdesk,
  experiments: Experiments,
  integrations: Integrations,
}

// Variants that draw their own <svg> (custom viewBox / aspect).
const SELF_FRAMED = { loop: GrowthLoop }

export function Mockup({ variant = "dashboard", className = "", "aria-label": ariaLabel }) {
  const Self = SELF_FRAMED[variant]
  if (Self) {
    return (
      <div role="img" aria-label={ariaLabel} className={`w-full h-full ${className}`}>
        <Self />
      </div>
    )
  }

  const Variant = VARIANTS[variant] || Dashboard
  return (
    <svg
      viewBox={FRAME}
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="xMidYMid slice"
      className={`w-full h-full ${className}`}
    >
      <Variant />
    </svg>
  )
}
